/**
 * <LiveSessionActionsSheet> — ce qu'on peut faire SUR cette séance (template).
 *
 * Le menu « ⋮ » a quitté l'espace parent : une séance se touche, et la feuille qui s'ouvre porte
 * son nom en titre — « Jeudi 17 septembre · 20h00 · Mathématiques avec Camille ». Le parent sait
 * donc de quelle séance on parle, et chaque geste part avec elle : on ne lui redemande pas
 * « Quelle séance ? », ni « Quel tuteur ? ».
 *
 * Coquille seule, comme `<NpsModal>` : le portail, le voile et les lignes viennent d'`<ActionSheet>`.
 * Ce qui vit ici, c'est la table des libellés et la règle de visibilité — les mêmes principes que
 * `<LiveSessionMenu>` : aucun appelant ne passe ses propres lignes, et la liste se dérive de l'état
 * de la séance.
 *
 * Une feuille d'actions est une LISTE d'actions, pas une étape de geste : elle peut en montrer plus
 * de deux (la règle des deux boutons, elle, vaut pour une étape — `CLAUDE.md`).
 *
 * **Deux rôles, une feuille.** La règle (`sessionActions`) dit ce que l'ÉTAT de la séance permet ;
 * `allowedActions` dit ce que le RÔLE a le droit de faire, et la feuille montre l'intersection.
 * Absent, rien n'est retiré : c'est le parent. L'élève passe `STUDENT_SESSION_ACTIONS` — il n'a ni
 * le composeur de message, ni l'annulation d'un rattrapage — et `voice="student"`, qui change le
 * seul libellé qui nomme quelqu'un : il entre lui-même en live, par son « GO ! ».
 */
import React from "react"

import { ActionSheet } from "../../molecules/ActionSheet"
import { type IconName } from "../../atoms/icons/Icon"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { elide } from "../../../ports/primary/ui/formatters/childFormatter"
import { sessionTitle } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { sessionActions, type LiveSessionAction } from "../../../ports/primary/ui/formatters/liveSessionActions"
import { PARENT_GESTURES } from "../../../ports/primary/ui/formatters/parentGestures"
import { type Voice } from "../../utils/voice"
import { actionsForRole, STUDENT_SESSION_ACTIONS, STUDENT_SESSION_ACTIONS_WITH_JOIN } from "./sheetActions"

export { STUDENT_SESSION_ACTIONS, STUDENT_SESSION_ACTIONS_WITH_JOIN }

/** Entrer en live : le parent bascule sur l'espace de son enfant, l'élève entre lui-même — son « GO ! ». */
const JOIN_LABEL: Record<Voice, (childFirstname: string | undefined) => string> = {
    parent: (childFirstname) => `Rejoindre en tant ${elide(childFirstname, "que")}`,
    student: () => "GO !",
}

/**
 * Libellé + glyphe par action.
 *
 * Deux gestes NOMMENT la personne qu'ils visent — « Écrire à Camille » dit à qui on écrit,
 * « Rejoindre en tant que Léa » dit sur quel espace on va passer — et leur libellé est donc une
 * fonction, composée au rendu. La table portait auparavant un libellé fixe pour eux deux
 * (« Message au tuteur », « Rejoindre ») qu'aucun écran n'a jamais rendu.
 *
 * `signal` est déjà le glyphe du live dans le kit (il remplace `live.png` sur la carte « temps
 * connecté au live » du bilan) : entrer en séance se dessine avec le même. Les trois gestes que les
 * tuiles de l'accueil offrent aussi prennent leur glyphe — et le libellé qu'ils partagent — dans
 * `PARENT_GESTURES`.
 */
type SheetLabel = string | ((session: UILiveSession, childFirstname: string | undefined, voice: Voice) => string)

const SHEET_ITEMS: Record<LiveSessionAction, { label: SheetLabel; icon: IconName }> = {
    join_as_child: { label: (_session, child, voice) => JOIN_LABEL[voice](child), icon: "signal" },
    move_session: { label: "Décaler cette séance", icon: PARENT_GESTURES.move_session.icon },
    report_absence: { label: "Prévenir d'une absence", icon: "times-circle" },
    report_late: { label: PARENT_GESTURES.report_late.label, icon: PARENT_GESTURES.report_late.icon },
    message_tutor: {
        label: (session) => `Écrire à ${session.tutor.firstname}`,
        icon: PARENT_GESTURES.message_tutor.icon,
    },
    cancel_absence: { label: "Annuler l'absence", icon: "undo-alt" },
    cancel_late: { label: "Annuler le retard", icon: "undo-alt" },
    cancel_recovery: { label: "Annuler le rattrapage", icon: "undo-alt" },
}

function sheetLabel(
    label: SheetLabel,
    session: UILiveSession,
    childFirstname: string | undefined,
    voice: Voice
): string {
    return typeof label === "function" ? label(session, childFirstname, voice) : label
}

export type LiveSessionActionsSheetProps = {
    visible: boolean
    onClose: () => void
    /** `null` ferme la feuille : la page n'a pas à garder une séance sélectionnée pour rien. */
    session: UILiveSession | null
    onAction: (action: LiveSessionAction) => void
    /**
     * Prénom de l'enfant quand le parent peut basculer sur son espace pour le faire entrer en
     * live. ABSENT = pas de ligne « Rejoindre » — c'est ainsi que l'appelant dit « cet enfant n'a
     * pas de jeton de bascule » ou « on est sous impersonation ». La feuille ne le calcule pas.
     */
    joinAsChildFirstname?: string
    /** À qui la feuille parle. L'élève entre lui-même en live : aucun prénom à passer. Défaut `"parent"`. */
    voice?: Voice
    /**
     * Les gestes que ce rôle a. La feuille ne montre que ceux-là, parmi ceux que l'état de la
     * séance permet. ABSENT = tous (le parent).
     */
    allowedActions?: readonly LiveSessionAction[]
    testID?: string
}

export function LiveSessionActionsSheet(props: LiveSessionActionsSheetProps) {
    const {
        visible,
        onClose,
        session,
        onAction,
        joinAsChildFirstname,
        voice = "parent",
        allowedActions,
        testID,
    } = props
    if (session == null) return null

    // L'élève est déjà sur son espace : il n'a pas de bascule à obtenir pour entrer.
    const canJoin = voice === "student" || joinAsChildFirstname != null

    const items = actionsForRole(sessionActions(session, canJoin), allowedActions).map((action) => ({
        icon: SHEET_ITEMS[action].icon,
        label: sheetLabel(SHEET_ITEMS[action].label, session, joinAsChildFirstname, voice),
        onPress: () => onAction(action),
    }))

    return (
        <ActionSheet
            visible={visible && items.length > 0}
            onClose={onClose}
            title={sessionTitle(session)}
            items={items}
            testID={testID}
        />
    )
}
